import React, { useMemo, useState } from "react";

import { Box, IconButton, MenuItem } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import ShipeeziNewGrid, {
  Column,
  ColumnFilter,
  GeneralActions,
  SearchSuggest,
} from "Components/AppGrid/shipeezi-new-grid";
import { colors } from "@zapalacorp/tokens";
import { useAppState } from "Context/StateContext";
import * as dot from "dot-wild";
import { Filters } from "Models/shipeezi.table";
import { useTranslation } from "react-i18next";
import RoleService from "Services/role.service";
import { Status, UserTypes } from "Util/enums";
import { TypeOfMember } from "Util/enums/members";
import { RoleKeys } from "Util/queries";

import { Color } from "../../../../../../../../components/Form/Button";
import { SplitButton } from "../../../../../../../../components/Form/SplitButton";
import IconCloseCircle from "../../../../../../../../components/Generic/Icons/CloseCircle";
import { Location } from "../../../../../../../../models/location";
import { SearchAttribute } from "../../../../../../../../models/search.attribute";
import { UserCompany } from "../../../../../../../../models/user.company";
import { filterSearch } from "../../../../../../../../util/filterSearch";
import { onSearch } from "../../../../../../../../util/onSearch";
import { paginate } from "../../../../../../../../util/paginate";
import { getTypes } from "../../../../People/Users/types";
import AddMemberModal from "../../SubcontractorsModal/AddMemberModal";
import { useSubcontractorContext } from "../context/Subcontractor";
import useStyles from "./styles";

type Props = {
  isView: boolean;
};

const Member: React.FC<Props> = ({ isView }) => {
  const { subcontractor, onUpdateSubcontractor, setSubcontractor } = useSubcontractorContext();
  const [isAddMemberModalOpened, setIsAddMemberModalOpened] = useState(false);

  const classes = useStyles();
  const { t: translate } = useTranslation();
  const [pageSize, setPageSize] = useState<number>(20);
  const [page, setPage] = useState<number>(0);
  const [filters, setFilters] = useState<Filters>();
  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const [searchAttributes, setSearchAttributes] = useState<SearchAttribute[]>([]);
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchOptions, setSearchOptions] = useState<ColumnFilter[]>([]);

  const { currentUser, isMovingVersion, isFMVersion } = useAppState();

  const membersAdded = useMemo(() => {
    return subcontractor?.members || [];
  }, [subcontractor]);

  const filtersMatcher = (object: any, filter: Filters): boolean => {
    const [filterKey, filterValues] = Object.entries(filter)[0];

    if (dot.has(object, filterKey)) {
      const value = dot.get(object, filterKey);

      return filterValues.some((filter) => filter === value);
    }

    return false;
  };

  const membersRenderable = useMemo(() => {
    let data = membersAdded || [];

    if (searchAttributes.length > 0) data = filterSearch(data, searchAttributes);

    if (filters) {
      for (const [filterKey, filterValue] of Object.entries(filters)) {
        data = data?.filter((dataItem) => filtersMatcher(dataItem, { [filterKey]: filterValue }));
      }
    }

    return searchAttributes.length > 0 ? data : paginate(data, page > 0 ? page - 1 : 0, pageSize);
  }, [membersAdded, searchAttributes, page, pageSize, filters]);

  const onAddMembersSelected = (members: any[]) => {
    const map: Map<number, any> = new Map();

    members.forEach((member) => {
      map.set(member.id, member);
    });

    onUpdateSubcontractor("members", Array.from(map.values()));
  };

  const onRemoveMemberSelected = (memberId: number) => {
    setSubcontractor((prev) => ({
      ...(prev || {}),
      members: (prev?.members || []).filter((member) => Number(member.id) !== Number(memberId)),
    }));
  };

  const { data: rolesList } = useQuery({
    queryKey: RoleKeys.getAll([]),
    queryFn: async () => {
      const response = await RoleService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
      });

      if (response && response?.data) return response.data;

      return [];
    },
    enabled: !!currentUser?.companyId,
    refetchOnWindowFocus: false,
  });

  const userTypes = getTypes(isMovingVersion, isFMVersion);

  const columns: Column[] = [
    {
      name: "userCompany",
      title: translate("code"),
      formatText: (userCompany?: UserCompany) => userCompany?.code || "-",
    },
    { name: "fullName", title: translate("name") },
    { name: "email", title: translate("email") },
    { name: "typeOfMember", title: translate("typeOfMember") },
    {
      name: "userCompany",
      title: translate("typeOfUser"),
      formatText: (userCompany?: UserCompany) => userCompany?.typeUser || "-",
    },
    {
      name: "userCompany",
      title: `${translate("role")}(${translate("security").toLowerCase()})`,
      formatText: (userCompany?: UserCompany) => userCompany?.role?.name || "-",
    },
    {
      name: "location",
      title: translate("location"),
      formatText: (location?: Location) =>
        location?.country || location?.state || location?.suburb || location?.addressLine1 || "-",
    },
  ];

  const onChangeSearchOptions = (options: ColumnFilter[]) => {
    setSearchOptions(options);
    const attributes = onSearch(options);

    setSearchAttributes(attributes);
  };

  const handleFilter = (value: Filters) => {
    const filtersKeys = Object.getOwnPropertyNames(value);

    setFilters(filtersKeys && filtersKeys?.length ? value : undefined);
  };

  const renderSearchSuggest = () => {
    return (
      <SearchSuggest
        columns={[
          { name: "name", title: translate("name") },
          { name: "email", title: translate("email") },
        ]}
        columnsSelected={searchOptions}
        onChangeColumnsSelected={onChangeSearchOptions}
        inputValue={searchInput}
        onChangeInput={setSearchInput}
        placeholder={translate("search")}
      />
    );
  };

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["searchSuggest", "handleFilterBy"],
      right: ["newRecord"],
    },
    searchSuggest: renderSearchSuggest(),
    handleFilterBy: {
      categories: [
        {
          name: translate("typeOfMember"),
          column: "typeOfMember",
          options: [TypeOfMember.STAFF, TypeOfMember.USER],
        },
        {
          name: translate("role"),
          column: "userCompany.role.name",
          options: rolesList?.map((role) => role.name) || [],
        },
        {
          name: translate("typeOfUser"),
          column: "userCompany.typeUser",
          options:
            userTypes
              ?.filter((type) => type.name !== UserTypes.STAFF)
              ?.map((userType) => userType.name) || [],
        },
      ],
      onChange: handleFilter,
    },
    newRecord: isView ? null : (
      <SplitButton
        customColor={Color.SECONDARY_DARK}
        onClick={() => setIsAddMemberModalOpened(true)}
        options={
          <>
            <MenuItem onClick={() => setIsAddMemberModalOpened(true)}>
              {translate("addAnExistentMember")}
            </MenuItem>
          </>
        }
      >
        {translate("addMember")}
      </SplitButton>
    ),
  };

  return (
    <Box className={classes.main}>
      <ShipeeziNewGrid
        data={membersRenderable}
        columns={columns}
        totalItems={membersAdded.length}
        loadingData={false}
        emptyData={translate("noMembersToShow")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        positionActionsColumn="right"
        generalActions={generalActions}
        rowActionsExtra={[
          {
            title: "remove",
            buttonAndAction: (memberId) =>
              isView ? null : (
                <IconButton onClick={() => onRemoveMemberSelected(memberId)}>
                  <IconCloseCircle width={20} height={20} color={colors.error500} />
                </IconButton>
              ),
          },
        ]}
        showSort={false}
      />
      <AddMemberModal
        isOpened={isAddMemberModalOpened}
        onClose={() => setIsAddMemberModalOpened(false)}
        membersIdAdded={membersAdded.map((member) => member.id)}
        onConfirmAddMembers={onAddMembersSelected}
      />
    </Box>
  );
};

export default Member;
